import { useCallback, useEffect, useState } from 'react';
import type { HealthResponse, MeResponse } from '@intentionalist/shared';
import { healthResponse, meResponse } from '@intentionalist/shared';
import { requestPersistentStorage, type StorageDurability } from './storage.js';
import { signOut, storedTokens } from './auth.js';
import { completeGoogleSignIn } from './oidc.js';
import { apiFetch, NotSignedIn } from './api.js';
import { EnvironmentBanner } from './EnvironmentBanner.js';
import { isProduction } from './environment.js';
import { Landing } from './Landing.js';
import { SignIn } from './SignIn.js';
import { Shell } from './Shell.js';

const API_BASE = import.meta.env['VITE_API_BASE_URL'] ?? '';

/**
 * The session, and nothing else.
 *
 * Everything that used to be here -- the account line, the reminder setting,
 * the build and storage readouts -- is now reached through `Shell`, because none
 * of it was what someone opened the app to look at. What is left is the one
 * decision this component is about: whether there is a session, and so whether
 * to show the sign-in form or the app.
 *
 * The landmark stays here. Everything is mounted inside this `<main>`, which is
 * what makes the screens navigable by a screen reader (NFR-UX-3); the panels
 * carry no landmark of their own, and the accessibility audit checks this shell
 * unwrapped for exactly that reason.
 *
 * `health` and `durability` are resolved here because they are asked once per
 * load, and handed to Diagnostics rather than queried where they are shown.
 */
export function App() {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [healthError, setHealthError] = useState<string | null>(null);
  const [durability, setDurability] = useState<StorageDurability | null>(null);

  const [me, setMe] = useState<MeResponse | null>(null);
  /** Null until the stored session has been checked, so the form does not flash. */
  const [resolved, setResolved] = useState(false);

  /**
   * Whether a signed-out visitor has asked to sign in.
   *
   * In the URL rather than in state alone, so the back button means something
   * here as it does inside the app: someone who presses Sign in and changes
   * their mind expects back to return them to the page, not to leave the site.
   */
  const [signingIn, setSigningIn] = useState(() =>
    new URLSearchParams(location.search).has('signin'),
  );

  useEffect(() => {
    const onPop = () => setSigningIn(new URLSearchParams(location.search).has('signin'));
    addEventListener('popstate', onPop);
    return () => removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    void requestPersistentStorage().then(setDurability);
  }, []);

  useEffect(() => {
    if (!API_BASE) {
      setHealthError('No API base URL configured for this build.');
      return;
    }
    const controller = new AbortController();
    fetch(`${API_BASE}/health`, { signal: controller.signal })
      .then((r) => r.json())
      .then((body) => setHealth(healthResponse.parse(body)))
      .catch((e: unknown) => {
        if (!controller.signal.aborted) {
          setHealthError(e instanceof Error ? e.message : 'Could not reach the service.');
        }
      });
    return () => controller.abort();
  }, []);

  /**
   * Confirms the session with the server rather than trusting what is in storage.
   *
   * A stored token can be expired or revoked, and `/me` is the cheapest way to
   * find out -- it goes through the same authorizer every real endpoint does.
   */
  const loadIdentity = useCallback(async () => {
    try {
      setMe(meResponse.parse(await apiFetch<unknown>('/me')));
    } catch (error) {
      if (error instanceof NotSignedIn) setMe(null);
      // Anything else is the service being unreachable, which is not a reason to
      // throw away a session that may still be good.
    } finally {
      setResolved(true);
    }
  }, []);

  useEffect(() => {
    /*
     * The hosted UI returns here with `?code=`. Redeeming it has to happen
     * before the stored-token check, or the first render after a Google
     * sign-in would conclude there is no session and show the landing page.
     */
    if (window.location.pathname === '/callback') {
      void completeGoogleSignIn()
        .then(() => {
          // replaceState, not pushState: a reload must not try to redeem a
          // code that has already been spent.
          history.replaceState({}, '', '/');
          return loadIdentity();
        })
        .catch(() => {
          history.replaceState({}, '', '/');
          setResolved(true);
        });
      return;
    }

    if (storedTokens() === null) {
      setResolved(true);
      return;
    }
    void loadIdentity();
  }, [loadIdentity]);

  return (
    <>
      {/* Outside `main`, so it is full width and reads as a fact about the
          deployment rather than about the catalog. */}
      {!isProduction() && <EnvironmentBanner />}

      <main>
        {!resolved ? (
          <p className="muted" role="status">
            checking your session…
          </p>
        ) : me === null ? (
          signingIn ? (
            <section aria-labelledby="signin-heading">
              <h1 id="signin-heading">Intentionalist</h1>
              <p className="lede">
                A manifest of what you own, and what you have decided about it.
              </p>
              <SignIn
                onSignedIn={() => void loadIdentity()}
                onBack={() => {
                  setSigningIn(false);
                  history.pushState({}, '', '/');
                }}
              />
            </section>
          ) : (
            <Landing
              onSignIn={() => {
                setSigningIn(true);
                history.pushState({ signin: true }, '', '/?signin');
              }}
            />
          )
        ) : (
          <Shell
            userId={me.userId}
            onSignedOut={() => {
              signOut();
              setMe(null);
            }}
            health={health}
            healthError={healthError}
            durability={durability}
            commit={__COMMIT_SHA__}
          />
        )}
      </main>
    </>
  );
}
